import { openDB, type IDBPDatabase } from 'idb'
import MiiaFwConfig from '@/configs/config'

interface ObjectStore<T> {
  key: string
  value: T
}

export interface tileStoreData {
  arrayBuffer: ArrayBuffer
  mine: string
  createdAt: number
  key: string
  url: string
  x: string
  y: string
  z: string
}

export interface LeafletOfflineDBWithCRUD {
  tileStore: ObjectStore<tileStoreData>
}

/**
 * indexedDBのStoreの定義を行う。
 */
export type LeafletOfflineDB = LeafletOfflineDBWithCRUD

// top-level awaitが使用できないため関数で代入する
let db: IDBPDatabase<LeafletOfflineDB>
const createDB = async (): Promise<void> => {
  if (db !== undefined) return
  // もしobjectStoreの構造を変更した場合、src/define/config.tsのMiiaFwConfig.config.disasterInv.indexedDBVersion.apiDBの値を1増加させる
  db = await openDB<LeafletOfflineDB>(
    'LeafletOfflineDB',
    MiiaFwConfig.config.disasterEmergency.indexedDBVersion.LeafletOfflineDB,
    {
      upgrade: async db => {
        try {
          // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
          if (db.objectStoreNames.contains('tileStore')) {
            db.deleteObjectStore('tileStore')
            db.deleteObjectStore('downloadedFeature')
          }

          db.createObjectStore('tileStore')
          db.createObjectStore('downloadedFeature')
        } catch (err) {
          if (err instanceof Error) {
            throw err
          }
        }
      }
    }
  )
}

/**
 * 指定したindexedDBの初期化処理
 */
export const init = async (): Promise<void> => {
  await createDB()
}

/**
 * 指定したindexedDBのobjectStoreにデータを挿入する
 * @param storeName ObjectStoreの名前
 * @param setData 挿入したいデータ
 * @param key プライマリキー
 */
export const set = async <T>(storeName: string, key: string, data: T): Promise<void> => {
  try {
    // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
    await createDB()
    await db.put(storeName, data, key).catch(async err => {
      throw err
    })
  } catch (err) {
    if (err instanceof Error) {
      throw err
    }
  }
}

/**
 * 指定したindexedDBのobjectStoreにデータを挿入する
 * @param storeName ObjectStoreの名前
 * @param setData 挿入したいデータ
 * @param key プライマリキー
 */
export const get = async <T>(storeName: string, key: string): Promise<T | undefined> => {
  try {
    // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
    await createDB()
    const ret = await db
      .get(storeName, key)
      .then(res => {
        return res as T
      })
      .catch(async err => {
        throw err
      })
    return ret
  } catch (err) {
    if (err instanceof Error) {
      throw err
    }
  }
}
/**
 * 指定したindexedDBのobjectStoreにデータのカウントを取得
 * @param storeName ObjectStoreの名前
 */
export const getCount = async (storeName: string): Promise<number | undefined> => {
  try {
    // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
    await createDB()
    const ret = await db
      .getAllKeys(storeName)
      .then(res => {
        return res.length
      })
      .catch(async err => {
        throw err
      })
    return ret
  } catch (err) {
    if (err instanceof Error) {
      throw err
    }
  }
}

/**
 * 指定したindexedDBのobjectStoreのすべてのデータを取得(キー名のみ)
 * @param storeName ObjectStoreの名前
 * @param setData 挿入したいデータ
 * @param key プライマリキー
 */
export const getKeys = async <T>(storeName: string): Promise<T[] | undefined> => {
  try {
    // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
    await createDB()
    const ret = await db
      .getAllKeys(storeName)
      .then(res => {
        return res as T[]
      })
      .catch(async err => {
        throw err
      })
    return ret
  } catch (err) {
    if (err instanceof Error) {
      throw err
    }
  }
}

/**
 * 指定したindexedDBのobjectStoreのすべてのデータを取得
 * @param storeName ObjectStoreの名前
 * @param setData 挿入したいデータ
 * @param key プライマリキー
 */
export const getAll = async <T>(storeName: string): Promise<T[] | undefined> => {
  try {
    // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
    await createDB()
    const ret = await db
      .getAll(storeName)
      .then(res => {
        return res as T[]
      })
      .catch(async err => {
        throw err
      })
    return ret
  } catch (err) {
    if (err instanceof Error) {
      throw err
    }
  }
}

/**
 * 指定したobjectStoreからデータをすべてのデータを削除する
 * @param storeName objectStoreの名前
 * @returns なし
 */
export const clear = async (storeName: string): Promise<void> => {
  await createDB()
  return await db.clear(storeName).catch(async err => {
    throw err
  })
}
